import { stringify } from 'qs';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import fetchWithDefaults from '../../CommonUtils/fetchWithDefaults';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { Sections } from '../components/ReportPreview/ReportPreview.meta';
import { useClientId } from '../../_hooks/useClientId';
import { useFpId } from '../../FinancialPlanOPL/hooks/useFpId';
import { fetch_data as fetchPDFData } from '../PDFActions';

interface Params {
  sections: Sections;
  isDraft: boolean;
  saveFile: boolean;
  customFileName: string | null;
}

export const saveReportPreviewRequest = ({ clientId, fpId }) => async ({
  sections,
  isDraft,
  saveFile,
  customFileName,
}: Params) => {
  const body = stringify({
    pdf: true,
    isDraft,
    saveFile,
    customFileName,
    json: JSON.stringify({
      includedSections: sections.included.filter(({ hidden }) => !hidden),
      excludedSections: sections.excluded.filter(({ hidden }) => !hidden),
    }),
    __RequestVerificationToken: ADV.settings.tokens.xsrf,
  });

  const queryParams = { clientId, fpId, cx: ADV.data.cx };

  // Using fetchWithDefaults instead of authFetch
  // Because this endpoint does not return a success response
  const response = await fetchWithDefaults(urlWithParams('/Consumer/Print', queryParams), {
    method: 'POST',
    body,
    headers: {
      'content-type': 'application/x-www-form-urlencoded',
      'x-requested-with': 'pdf',
    },
  });

  if (response.ok) {
    const fileInfoHeader = response.headers.get('content-disposition');

    if (fileInfoHeader) {
      const fileName = decodeURIComponent(fileInfoHeader.split('=')[1])
        .replace("UTF-8''", '')
        .replace('.pdf', '');
      const fileBlob = await response.blob();
      const blob = new Blob([fileBlob], { type: 'application/pdf' });

      const fileUrl = URL.createObjectURL(blob);

      return { fileName, fileUrl, fileBlob };
    }
  }

  throw new Error(
    'The PDF failed to generate. Please try again, and contact us if you continue to have this issue.',
  );
};

export const useReportPreviewMutation = () => {
  const fpId = useFpId();
  const clientId = useClientId();
  const dispatch = useDispatch();

  return useMutation(saveReportPreviewRequest({ fpId, clientId }), {
    onSuccess: (_, { saveFile }) => {
      if (saveFile) {
        dispatch(fetchPDFData(clientId));
      }
    },
  });
};
